import * as React from 'react';
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonIcon,
    IonText,
} from '@ionic/react';
import { calendarOutline, locationOutline, timeOutline } from 'ionicons/icons';
import Page from '../../../components/core/Page';
import EventAddToCalendarButton from '../../../components/events/EventAddToCalendarButton';
import { EventDetail, EventAllocation, Facility } from '../../../models';
import { Ages } from '../../../utils/ages';
import TicketCard from './TicketCard/TicketCard';
import './events-page.component.css';

interface EventsPageProps {
    event?: EventDetail;
    allocations: EventAllocation[];
    isLoading: boolean;
    errorMessage?: string;
    manageBookingsProfilePackageId: number | null;
    canManageBookings: boolean;
    canViewBookings: boolean;
}

export const EventsPage: React.FC<EventsPageProps> = ({
    event,
    allocations,
    isLoading,
    errorMessage,
    manageBookingsProfilePackageId,
    canManageBookings,
    canViewBookings,
}) => {
    const formatFacilityAddress = (facility: Facility): string => {
        return [facility.street1, facility.street2, facility.suburb, facility.city, facility.province]
            .filter(Boolean)
            .join(', ');
    };

    const formatEventDate = () => {
        if (!event?.startDateTs) return undefined;
        const start = new Ages(event.startDateTs).toFormat('dd mmm yyyy');
        if (!event.endDateTs || event.startDateTs === event.endDateTs) return start;

        const end = new Ages(event.endDateTs).toFormat('dd mmm yyyy');
        if (start === end) return start;

        return `${start} – ${end}`;
    };

    const formatEventTime = () => {
        if (!event?.startDateTs) return undefined;
        // If start and end are the same, only show date
        if (!event.endDateTs || event.startDateTs === event.endDateTs) return undefined;

        const startTime = new Ages(event.startDateTs).toFormat('hh:ii');
        const endTime = new Ages(event.endDateTs).toFormat('hh:ii');

        // If both are 00:00, it's likely just a date range without time
        if (startTime === '00:00' && endTime === '00:00') return undefined;

        return `${startTime} – ${endTime}`;
    };

    return (
        <Page
            title={event?.name || 'Event'}
            backRouterLink="/home"
            showMenuButton={false}
            showLoading={isLoading}
        >
            <div className="events-page-content ion-padding">
                {errorMessage && (
                    <IonText color="danger">
                        <p>{errorMessage}</p>
                    </IonText>
                )}

                {event && !isLoading && (
                    <>
                        <IonCard className="event-details-card">
                            <IonCardContent>
                                <IonText>
                                    <h2 className="event-title">{event.name}</h2>
                                </IonText>
                                {formatEventDate() && (
                                    <div className="event-meta-row">
                                        <IonIcon icon={calendarOutline} />
                                        <IonText color="medium">
                                            <p>
                                                {formatEventDate()}
                                                {formatEventTime() && `, ${formatEventTime()}`}
                                            </p>
                                        </IonText>
                                    </div>
                                )}
                                {event.slots && event.slots.length === 1 && event.slots[0].facility && (
                                    <div className="event-meta-row">
                                        <IonIcon icon={locationOutline} />
                                        <IonText color="medium">
                                            <p>
                                                {event.slots[0].facility.name}
                                                {formatFacilityAddress(event.slots[0].facility) && `, ${formatFacilityAddress(event.slots[0].facility)}`}
                                            </p>
                                        </IonText>
                                    </div>
                                )}
                                {event.slots && event.slots.length > 1 && (
                                    <div className="event-slots-container">
                                        {event.slots.map((slot, index) => (
                                            <div key={index}>
                                                <div className="event-meta-row">
                                                    <IonIcon icon={timeOutline} />
                                                    <IonText color="medium">
                                                        <p>{slot.displayTime}</p>
                                                    </IonText>
                                                </div>
                                                {slot.facility && (
                                                    <div className="event-meta-row event-meta-row--indented">
                                                        <IonIcon icon={locationOutline} />
                                                        <IonText color="medium">
                                                            <p>
                                                                {slot.facility.name}
                                                                {formatFacilityAddress(slot.facility) && `, ${formatFacilityAddress(slot.facility)}`}
                                                            </p>
                                                        </IonText>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {event.description && (
                                    <IonText color="medium">
                                        <p className="event-description">{event.description}</p>
                                    </IonText>
                                )}
                            </IonCardContent>
                        </IonCard>

                        {event.startDateTs && (
                            <EventAddToCalendarButton
                                event={event}
                                expand="block"
                                fill="outline"
                            />
                        )}

                        <IonText color="medium">
                            <p className="tickets-section-label">YOUR TICKETS</p>
                        </IonText>

                        {allocations.length === 0 && (
                            <IonText>
                                <p>No tickets found for this event.</p>
                            </IonText>
                        )}

                        {allocations.map((allocation, index) => (
                            <TicketCard
                                key={allocation.uuid}
                                allocation={allocation}
                                ticketNumber={index + 1}
                            />
                        ))}

                        {event.hasSlots && manageBookingsProfilePackageId != null && (canManageBookings || canViewBookings) && (
                            <IonButton
                                expand="block"
                                fill="outline"
                                className="ion-margin-top"
                                routerLink={`/slots/${manageBookingsProfilePackageId}`}
                            >
                                {canManageBookings ? 'Manage Bookings' : 'View Bookings'}
                            </IonButton>
                        )}
                    </>
                )}
            </div>
        </Page>
    );
};
